import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { oktaConfig } from "../config/auth";
import { verifyOktaWithBackend } from "../services/authService";
import { clearAuthSession, setAuthSession } from "../services/authSessionService";
import { runAuthorizationCodeExchangeOnce } from "../utils/authorizationCodeSingleFlight";
import { mapSignInFlowError } from "../utils/oktaSignInErrors";
import { logHostDocumentFileName } from "../services/hostDocumentInfoService";

type CallbackState = "loading" | "success" | "error";

export const OktaCallbackPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackState>("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    const exchange = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");
      const error = params.get("error");
      const errorDesc = params.get("error_description");

      // If callback is opened in Office dialog, pass result to taskpane parent and exit.
      if (
        typeof Office !== "undefined" &&
        Office?.context?.ui?.messageParent
      ) {
        Office.context.ui.messageParent(
          JSON.stringify({
            code: code ?? "",
            state: state ?? "",
            error: error ?? "",
            error_description: errorDesc ?? "",
          })
        );
        if (!cancelled) {
          setStatus("success");
        }
        return;
      }

      if (error) {
        clearAuthSession();
        setStatus("error");
        setErrorMessage(`Okta error: ${error}${errorDesc ? ` - ${errorDesc}` : ""}`);
        return;
      }
      if (!code) {
        clearAuthSession();
        setStatus("error");
        setErrorMessage("No authorization code received from Okta.");
        return;
      }

      const expectedState = sessionStorage.getItem("pkce_state");
      if (state !== expectedState) {
        clearAuthSession();
        setStatus("error");
        setErrorMessage("State parameter mismatch. Please try logging in again.");
        return;
      }

      const codeVerifier = sessionStorage.getItem("pkce_code_verifier");
      if (!codeVerifier) {
        clearAuthSession();
        setStatus("error");
        setErrorMessage("Missing PKCE verifier. Please try logging in again.");
        return;
      }

      const { issuer, clientId, redirectUri } = oktaConfig;
      try {
        await runAuthorizationCodeExchangeOnce(code, async () => {
          const body = new URLSearchParams({
            grant_type: "authorization_code",
            code,
            redirect_uri: redirectUri,
            client_id: clientId,
            code_verifier: codeVerifier,
          });

          const res = await fetch(`${issuer}/v1/token`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: body.toString(),
          });
          if (!res.ok) {
            const errBody = await res.text();
            throw new Error(`Token exchange failed (${res.status}): ${errBody}`);
          }
          const tokens = await res.json();
          const accessToken = String(tokens.access_token ?? "");
          const idToken = String(tokens.id_token ?? "");
          if (!accessToken && !idToken) {
            throw new Error("Token response had no access_token or id_token.");
          }

          const expiresAt =
            tokens.expires_in != null ? Date.now() + Number(tokens.expires_in) * 1000 : null;

          sessionStorage.removeItem("pkce_code_verifier");
          sessionStorage.removeItem("pkce_state");

          const verified = await verifyOktaWithBackend(accessToken, idToken);
          setAuthSession({
            accessToken,
            idToken,
            expiresAt,
            user: verified.user,
            effectiveAccess: verified.effective_access,
          });
          void logHostDocumentFileName("after Okta sign-in (callback)");
        });

        if (!cancelled) {
          setStatus("success");
          setTimeout(() => navigate("/", { replace: true }), 300);
        }
      } catch (err) {
        clearAuthSession();
        if (!cancelled) {
          setStatus("error");
          setErrorMessage(mapSignInFlowError(err));
        }
      }
    };

    void exchange();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-sm">
        {status === "loading" && (
          <>
            <h1 className="mb-2 text-lg font-semibold text-foreground">Completing sign-in</h1>
            <p className="text-sm text-muted-foreground">Exchanging authorization code for tokens.</p>
          </>
        )}
        {status === "success" && (
          <>
            <h1 className="mb-2 text-lg font-semibold text-foreground">Sign-in successful</h1>
            <p className="text-sm text-muted-foreground">Redirecting to the add-in...</p>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="mb-2 text-lg font-semibold text-destructive">Sign-in failed</h1>
            <p className="mb-4 text-sm text-muted-foreground">{errorMessage}</p>
            <button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
};
